/**
 * Admin Layout
 * Layout base para o painel administrativo - estilo vover
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  MoreVert,
  Dashboard,
  Assessment,
  PersonSearch,
  People,
  AccountBalance,
  TrendingUp,
  Settings,
  ReceiptLong,
  Apps
} from '@mui/icons-material';
import { useRefresh } from '@/contexts/RefreshContext';
import { useFilters } from '@/contexts/FilterContext';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import Logo from '@/components/Logo';
import RefreshButton from '@/components/RefreshButton';
import UserMenu from '@/components/UserMenu';
import AdvancedFilters from '@/components/AdvancedFilters';
import { ProductFilter } from '@/components/ProductFilter';
import {
  getProductFilterMode,
  isPlatformProductScopedTab,
  platformProductsTab
} from '@/config/productFilters';

interface MenuItemType {
  text: string;
  icon: React.ReactElement;
  path: string;
  voverAppOnly?: boolean;
  platformOnly?: boolean;
  permission?: string;
}

const baseMenuItems: MenuItemType[] = [
  { text: 'Painel', icon: <Dashboard />, path: '/', permission: 'tenants:read' },
  {
    text: 'Auditoria da plataforma',
    icon: <Assessment />,
    path: '/audit-logs',
    permission: 'audit:read'
  },
  { text: 'Personificação', icon: <PersonSearch />, path: '/impersonation' },
  {
    text: 'Fluxo Financeiro',
    icon: <AccountBalance />,
    path: '/financial-flow',
    permission: 'billing:read'
  },
  {
    text: 'Métricas Billing',
    icon: <TrendingUp />,
    path: '/billing-metrics',
    permission: 'billing:read'
  },
  {
    text: 'Observabilidade NFS-e',
    icon: <ReceiptLong />,
    path: '/nfse-observability',
    permission: 'billing:read'
  },
  { text: 'Produtos', icon: <Apps />, path: '/platform/products', permission: 'tenants:read' },
  {
    text: 'Usuários',
    icon: <People />,
    path: '/users',
    permission: 'tenants:read',
    voverAppOnly: true
  },
  { text: 'Configurações', icon: <Settings />, path: '/configurations' }
];

// Páginas que devem mostrar o filtro de clientes
const pagesWithClientFilter = [
  '/tenants',
  '/users',
  '/audit-logs',
  '/impersonation',
  '/nfse-observability'
];

export const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh, isRefreshing } = useRefresh();
  const { selectedClients, setSelectedClients } = useFilters();
  const { can, isVoverApp } = useAdminPermissions();
  const productTab = platformProductsTab(location.search);
  const productFilterMode = getProductFilterMode(location.pathname, location.search);
  const showProductFilter = productFilterMode !== 'hidden';

  const menuItems = baseMenuItems.filter(item => {
    if (item.permission && !can(item.permission)) return false;
    if (item.voverAppOnly && !isVoverApp) return false;
    return true;
  });

  const open = Boolean(anchorEl);
  const showClientFilter =
    pagesWithClientFilter.includes(location.pathname) ||
    (location.pathname === '/platform/products' && isPlatformProductScopedTab(productTab));

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: 'linear-gradient(90deg, #f7f8fc 0%, #d1d8e4 100%)',
          color: '#334155',
          borderBottom: '1px solid #e2e8f0',
          zIndex: theme => theme.zIndex.drawer + 1,
          width: '100%',
          top: 0
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 1, sm: 2 },
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {/* Logo com texto admin */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mr: { xs: 1, sm: 2 },
              minWidth: { xs: 'auto', sm: 'auto' }
            }}
            onClick={() => navigate('/')}
          >
            <Logo
              height={isMobile ? 40 : 52}
              variant="auto"
              color="default"
              showAdminText={true}
              clickable={true}
              onClick={() => navigate('/')}
              sx={{ mr: { xs: 0.5, sm: 1 } }}
            />
          </Box>

          {showProductFilter && <ProductFilter mode={productFilterMode} />}

          <Box sx={{ flexGrow: 1 }} />

          {/* Filtros avançados - apenas em desktop e em páginas específicas */}
          {!isMobile && showClientFilter && (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <AdvancedFilters
                config={{
                  showClientFilter: true,
                  showSearchFilter: false,
                  variant: 'default',
                  pageType: location.pathname
                }}
                selectedClients={selectedClients}
                onClientsChange={setSelectedClients}
              />
            </Box>
          )}

          {/* Botão de Refresh */}
          <RefreshButton onRefresh={refresh} loading={isRefreshing} />

          {/* Menu de Navegação */}
          <IconButton
            size={isMobile ? 'small' : 'large'}
            aria-label="menu"
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            onClick={handleMenuClick}
            color="inherit"
            sx={{ mr: { xs: 0.5, sm: 1 } }}
          >
            <MoreVert fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
          >
            {menuItems.map(item => (
              <MenuItem
                key={item.path}
                onClick={() => handleMenuItemClick(item.path)}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>
                  {React.isValidElement(item.icon)
                    ? React.cloneElement(item.icon, { fontSize: 'small' } as any)
                    : item.icon}
                </ListItemIcon>
                <ListItemText>{item.text}</ListItemText>
              </MenuItem>
            ))}
          </Menu>

          {/* Menu do Usuário */}
          <UserMenu />
        </Toolbar>
      </AppBar>

      {/* Conteúdo principal */}
      <Box sx={{ mt: { xs: 7, sm: 8 }, p: 3 }}>{children}</Box>
    </Box>
  );
};
